import { useEffect, useState } from "react";

import { DataTable, type DataTableColumn } from "@/components/cinch/DataTable/DataTable";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useCan } from "@/lib/auth";

import {
  useAssumptions,
  useChangeLog,
  useUpdateCollectionsConfig,
  type ChangeLogEntry,
  type ForecastDriver,
  type NewHireRow,
  type NewRevenueRow,
  type PayScheduleRow,
  type RecurringVendorRow,
} from "../api";
import { fmtUsd, weekLabel } from "../lib";
import { RollHorizonButton } from "./RollHorizonDialog";

const METHOD_LABEL: Record<ForecastDriver["method"], string> = {
  manual: "Manual",
  seasonality: "Seasonality",
  pay_schedule: "Pay calendar",
  recurring_vendors: "Vendor calendar",
  new_revenue: "New contract",
};

/** The collections look-back — the one assumption Jim flagged as unsettled.
 *
 *  On the 2026-08-06 call: "maybe it needs to be a shorter horizon, like maybe
 *  only six week look back because sales are increasing so rapidly". So it's a
 *  setting, and changing it is logged, because it moves the entire Customer
 *  Collections forecast.
 */
function CollectionsCard() {
  const { data } = useAssumptions();
  const update = useUpdateCollectionsConfig();
  const canWrite = useCan("cash_flow.write");
  const config = data?.collections_config;
  const [weeks, setWeeks] = useState("");

  useEffect(() => {
    if (config) setWeeks(String(config.trailing_weeks));
  }, [config]);

  const parsed = Number(weeks);
  const valid = Number.isInteger(parsed) && parsed >= 2 && parsed <= 52;
  const dirty = valid && config && parsed !== config.trailing_weeks;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Customer Collections run rate</CardTitle>
        <CardDescription>
          Trailing bank actuals, net of new-contract cash already landed in the window,
          annualised on a calendar-month basis (×52/12 — <em>not</em> ×4, or the
          calendar-normalised weights double-adjust and skew the forecast low), then multiplied
          by the week-slot weight below.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="trailing-weeks">Look-back (weeks)</Label>
            <Input
              id="trailing-weeks"
              type="number"
              min={2}
              max={52}
              value={weeks}
              onChange={(e) => setWeeks(e.target.value)}
              className="w-32"
            />
          </div>
          <Button
            disabled={!canWrite || !dirty || update.isPending}
            onClick={() =>
              update.mutate({
                trailing_weeks: parsed,
                why: `Look-back changed from ${config?.trailing_weeks} to ${parsed} weeks`,
              })
            }
          >
            {update.isPending ? "Saving…" : "Save"}
          </Button>
          <p className="text-xs text-muted-foreground">
            13 is the current setting and Jim is explicitly unsure it's right — sales are
            growing fast enough that a shorter window may track better. A narrower 4-week cut is
            noisier; 8 was chosen for stability once before.
          </p>
        </div>
        {!valid && weeks !== "" && (
          <p className="text-sm text-danger">Enter a whole number between 2 and 52.</p>
        )}
        <div className="flex flex-wrap gap-2">
          {(data?.seasonality_weights ?? []).map((w) => (
            <Badge key={w.id} variant="neutral">
              {w.slot} (day {w.day_of_month}+): {w.weight}
            </Badge>
          ))}
        </div>
        <p className="text-xs text-muted-foreground">
          Weights are calendar-normalised on purpose: a four-week month sums to 0.890 and a
          five-week month to 1.221, so run rate × weight lands on the right monthly total.
          They are not meant to sum to 1.
        </p>
      </CardContent>
    </Card>
  );
}

function DriversCard() {
  const { data, isLoading } = useAssumptions();
  const canWrite = useCan("cash_flow.write");
  const columns: DataTableColumn<ForecastDriver>[] = [
    { key: "bucket_name", header: "Bucket", render: (d) => d.bucket_name ?? "—" },
    { key: "label", header: "Detail" },
    {
      key: "method",
      header: "Source",
      render: (d) => (
        <Badge variant={d.method === "manual" ? "neutral" : "info"}>
          {METHOD_LABEL[d.method]}
        </Badge>
      ),
    },
    {
      key: "weeks",
      header: "Weeks set",
      numeric: true,
      align: "right",
      render: (d) => (d.method === "manual" ? d.weeks.length : "computed"),
    },
    {
      key: "sort_order",
      header: "Next amount",
      numeric: true,
      align: "right",
      render: (d) => {
        const next = d.weeks.find((w) => w.week_ending >= new Date().toISOString().slice(0, 10));
        return next ? `${fmtUsd(next.amount)} · ${weekLabel(next.week_ending)}` : "—";
      },
    },
  ];
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle>Forecast drivers</CardTitle>
            <CardDescription>
          The workbook's Schedule tab. Every row tagged with a bucket feeds that bucket's weekly
          forecast, so adding a recurring item is a new row rather than a formula change.
          Manual rows carry stored weekly amounts; the rest are computed from the tables below —
          deliberately, because a computed row that silently became a hardcoded one is what
              caused a 48.5% miss on collections in July 2026.
            </CardDescription>
          </div>
          {canWrite && <RollHorizonButton />}
        </div>
      </CardHeader>
      <CardContent>
        <DataTable
          columns={columns}
          rows={data?.drivers ?? []}
          rowKey={(d) => d.id}
          loading={isLoading}
          emptyMessage="No drivers configured."
        />
      </CardContent>
    </Card>
  );
}

function PayScheduleCard() {
  const { data, isLoading } = useAssumptions();
  const columns: DataTableColumn<PayScheduleRow>[] = [
    {
      key: "period_start",
      header: "Pay period",
      render: (p) => `${weekLabel(p.period_start)} – ${weekLabel(p.period_end)}`,
    },
    { key: "pay_type", header: "Type", render: (p) => <Badge variant="neutral">{p.pay_type}</Badge> },
    { key: "payment_date", header: "Payment date", render: (p) => weekLabel(p.payment_date, true) },
    {
      key: "cash_week",
      header: "Cash week",
      render: (p) => (
        <span className={p.cash_week < p.payment_date ? "font-medium text-warning" : undefined}>
          {weekLabel(p.cash_week, true)}
        </span>
      ),
    },
    {
      key: "forecast_amount",
      header: "Amount",
      numeric: true,
      align: "right",
      render: (p) => <span className="text-danger">{fmtUsd(p.forecast_amount)}</span>,
    },
  ];
  return (
    <Card>
      <CardHeader>
        <CardTitle>Payroll calendar</CardTitle>
        <CardDescription>
          The real Rippling schedule through April 2027. <b>Cash week is stored, not derived</b>:
          end-of-month runs clear in the first week of the following month, so mapping a payment
          date to its own calendar week puts payroll in the wrong place — which it did for most
          of July 2026. Highlighted cash weeks are the ones that precede their payment date.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <DataTable
          columns={columns}
          rows={data?.pay_schedule ?? []}
          rowKey={(p) => p.id}
          loading={isLoading}
          emptyMessage="No pay schedule loaded."
        />
      </CardContent>
    </Card>
  );
}

function ContractsCard() {
  const { data, isLoading } = useAssumptions();
  const columns: DataTableColumn<NewRevenueRow>[] = [
    { key: "contract", header: "Contract" },
    {
      key: "annual_amount",
      header: "Annual",
      numeric: true,
      align: "right",
      render: (r) => fmtUsd(r.annual_amount),
    },
    { key: "start_date", header: "Start", render: (r) => weekLabel(r.start_date, true) },
    {
      key: "terms_days",
      header: "Terms + buffer",
      render: (r) => `${r.terms_days}d + ${r.buffer_days}d`,
    },
    {
      key: "first_cash_week",
      header: "First cash",
      render: (r) => weekLabel(r.first_cash_week, true),
    },
    {
      key: "notes",
      header: "Basis",
      render: (r) => (
        <span className="block max-w-sm truncate text-xs text-muted-foreground" title={r.notes ?? ""}>
          {r.notes ?? "—"}
        </span>
      ),
    },
  ];
  return (
    <Card>
      <CardHeader>
        <CardTitle>New contracts not yet in the run rate</CardTitle>
        <CardDescription>
          Signed business layered on top of the collections base. First cash = start + terms +
          a judgement buffer for known-late payers, then recurring every 28 days. Worth checking
          against actuals as each one lands.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <DataTable
          columns={columns}
          rows={data?.new_revenue ?? []}
          rowKey={(r) => r.id}
          loading={isLoading}
          emptyMessage="No contracts layered in."
        />
      </CardContent>
    </Card>
  );
}

function HiresAndVendorsCard() {
  const { data, isLoading } = useAssumptions();
  const hireColumns: DataTableColumn<NewHireRow>[] = [
    { key: "name", header: "Hire" },
    { key: "start_date", header: "Start", render: (h) => weekLabel(h.start_date, true) },
    {
      key: "add_per_paycheck",
      header: "Per paycheck",
      numeric: true,
      align: "right",
      render: (h) => <span className="text-danger">{fmtUsd(h.add_per_paycheck)}</span>,
    },
    {
      key: "note",
      header: "Note",
      render: (h) => <span className="text-xs text-muted-foreground">{h.note ?? "—"}</span>,
    },
  ];
  const vendorColumns: DataTableColumn<RecurringVendorRow>[] = [
    { key: "vendor", header: "Vendor" },
    { key: "due_date", header: "Due", render: (v) => weekLabel(v.due_date, true) },
    { key: "cash_week", header: "Cash week", render: (v) => weekLabel(v.cash_week, true) },
    {
      key: "forecast_amount",
      header: "Amount",
      numeric: true,
      align: "right",
      render: (v) => (
        <span className={v.forecast_amount < 0 ? "text-danger" : undefined}>
          {fmtUsd(v.forecast_amount)}
        </span>
      ),
    },
  ];
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle>New hires</CardTitle>
          <CardDescription>Added to each paycheck once they've started.</CardDescription>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={hireColumns}
            rows={data?.new_hires ?? []}
            rowKey={(h) => h.id}
            loading={isLoading}
            emptyMessage="No hires layered in."
          />
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Recurring vendors</CardTitle>
          <CardDescription>
            One row per expected payment, not per vendor — amounts and timing move month to
            month.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={vendorColumns}
            rows={data?.recurring_vendors ?? []}
            rowKey={(v) => v.id}
            loading={isLoading}
            emptyMessage="No vendor payments scheduled."
          />
        </CardContent>
      </Card>
    </div>
  );
}

/** Jim's ask, verbatim: "I need you to log it so I can look back and go, what
 *  day did I do that?" Written automatically on every rule and assumption
 *  edit rather than when someone remembers. */
function ChangeLogCard() {
  const { data, isLoading } = useChangeLog();
  const columns: DataTableColumn<ChangeLogEntry>[] = [
    { key: "changed_on", header: "Date", render: (c) => weekLabel(c.changed_on, true) },
    {
      key: "change",
      header: "Change",
      render: (c) => <span className="block max-w-lg text-sm">{c.change}</span>,
    },
    {
      key: "why",
      header: "Why",
      render: (c) => (
        <span className="block max-w-md text-xs text-muted-foreground">{c.why ?? "—"}</span>
      ),
    },
    {
      key: "impact",
      header: "Impact",
      render: (c) => (
        <span className="block max-w-md text-xs text-muted-foreground">{c.impact ?? "—"}</span>
      ),
    },
    {
      key: "author_name",
      header: "By",
      render: (c) => <span className="text-xs">{c.author_name ?? "—"}</span>,
    },
  ];
  return (
    <Card>
      <CardHeader>
        <CardTitle>Model change log</CardTitle>
        <CardDescription>
          What changed, why, and what it did to the numbers. Seeded with the workbook's own
          history so the reasoning behind the current model survives the port, and appended to
          automatically on every rule or assumption edit.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <DataTable
          columns={columns}
          rows={data ?? []}
          rowKey={(c) => c.id}
          loading={isLoading}
          emptyMessage="No changes logged."
        />
      </CardContent>
    </Card>
  );
}

/** The CFO-controlled inputs behind the forecast — the workbook's Assumptions
 *  tab. Read-only for now apart from the collections look-back; per-row
 *  editing of the pay calendar, hires, contracts and vendors is the next
 *  increment. */
export function AssumptionsTab() {
  return (
    <div className="flex flex-col gap-6">
      <CollectionsCard />
      <DriversCard />
      <PayScheduleCard />
      <ContractsCard />
      <HiresAndVendorsCard />
      <ChangeLogCard />
    </div>
  );
}
